/**
 * Gemini Flash Lite API client for label extraction.
 *
 * Takes a base64 image and returns structured data:
 * {
 *   title: "HP ProBook 450 G9",
 *   details: { "S/N": "...", "P/N": "...", "WLAN MAC": "...", "LAN MAC": "..." }
 * }
 *
 * When VITE_PROXY_URL is set at build time, calls go through the backend proxy
 * (Gemini API key stays server-side). Otherwise falls back to direct calls using
 * the key stored in Settings.
 */

import { getSetting } from './settings.js';

const EXTRACTION_PROMPT = `You are an IT asset label reader. Analyze this device label photo and extract structured data.

Return ONLY valid JSON in this exact format (no markdown, no explanation):
{
  "title": "<Device manufacturer and model name>",
  "details": {
    "S/N": "<serial number or empty string>",
    "P/N": "<product/part number or empty string>",
    "LAN MAC": "<ethernet MAC address or empty string>",
    "WLAN MAC": "<wireless MAC address or empty string>"
  }
}

If only one MAC address or MAC ID is present and it is not explicitly labeled as WLAN, Wi-Fi, or Wireless, place it in "LAN MAC" and leave "WLAN MAC" as an empty string.
If you cannot identify a field, use an empty string. Always return valid JSON.`;

export async function extractFromImage(base64Image, mimeType = 'image/jpeg') {
  const proxyUrl = import.meta.env.VITE_PROXY_URL;

  if (proxyUrl) {
    const res = await fetch(`${proxyUrl.replace(/\/+$/, '')}/api/gemini/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64Image, mimeType }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `Gemini proxy error: ${res.status}`);
    }
    return parseGeminiResponse(await res.json());
  }

  // Fallback: direct call using key from Settings
  const apiKey = getSetting('geminiApiKey');
  if (!apiKey) {
    throw new Error('Gemini API key not configured. Go to Settings to add it.');
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [
        {
          parts: [
            { text: EXTRACTION_PROMPT },
            {
              inline_data: {
                mime_type: mimeType,
                data: base64Image,
              },
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.1,
        maxOutputTokens: 512,
      },
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gemini API error: ${response.status}`);
  }

  return parseGeminiResponse(await response.json());
}

const MULTI_IMAGE_PROMPT = `You are an IT asset label reader. You are given multiple photos of the SAME device taken from different angles.

Cross-reference ALL images to find the clearest reading for each field. If a value appears in multiple photos, prefer the most legible one. If only one photo contains a field, use that.

Return ONLY valid JSON in this exact format (no markdown, no explanation):
{
  "title": "<Device manufacturer and model name>",
  "details": {
    "S/N": "<serial number or empty string>",
    "P/N": "<product/part number or empty string>",
    "LAN MAC": "<ethernet MAC address or empty string>",
    "WLAN MAC": "<wireless MAC address or empty string>"
  }
}

If only one MAC address or MAC ID is present and it is not explicitly labeled as WLAN, Wi-Fi, or Wireless, place it in "LAN MAC" and leave "WLAN MAC" as an empty string.
If you cannot identify a field, use an empty string. Always return valid JSON.`;

/**
 * Extract asset data from multiple images of the same device.
 * Sends all images in a single Gemini call for cross-referencing.
 * @param {Array<{base64: string, mimeType: string}>} images
 * @returns {Promise<{title: string, details: object}>}
 */
export async function extractFromMultipleImages(images) {
  const proxyUrl = import.meta.env.VITE_PROXY_URL;

  if (proxyUrl) {
    const res = await fetch(`${proxyUrl.replace(/\/+$/, '')}/api/gemini/extract-multi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ images }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `Gemini proxy error: ${res.status}`);
    }
    return parseGeminiResponse(await res.json());
  }

  // Fallback: direct call
  const apiKey = getSetting('geminiApiKey');
  if (!apiKey) {
    throw new Error('Gemini API key not configured. Go to Settings to add it.');
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`;

  const parts = [
    { text: MULTI_IMAGE_PROMPT },
    ...images.map((img) => ({
      inline_data: { mime_type: img.mimeType, data: img.base64 },
    })),
  ];

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts }],
      generationConfig: { temperature: 0.1, maxOutputTokens: 512 },
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gemini API error: ${response.status}`);
  }

  return parseGeminiResponse(await response.json());
}

function parseGeminiResponse(json) {
  const text = json.candidates?.[0]?.content?.parts?.[0]?.text || '';
  const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    throw new Error('Failed to parse Gemini response as JSON. Raw: ' + text.slice(0, 200));
  }
}

/**
 * Convert a File/Blob to a base64 string (without the data URI prefix).
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Strip "data:image/jpeg;base64," prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale an image blob so the longest edge is at most maxEdge pixels,
 * then return { base64, mimeType } ready for Gemini API.
 * If the image is already within bounds, encodes at 92% JPEG without resizing.
 */
export async function prepareForGemini(blob, maxEdge = 2560) {
  const bitmap = await createImageBitmap(blob);
  let { width, height } = bitmap;

  if (width > maxEdge || height > maxEdge) {
    const scale = maxEdge / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return new Promise((resolve) => {
    canvas.toBlob(
      (resized) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            base64: reader.result.split(',')[1],
            mimeType: 'image/jpeg',
          });
        reader.readAsDataURL(resized);
      },
      'image/jpeg',
      0.92
    );
  });
}
